/**
 * Argon2id key derivation via WASM (hash-wasm).
 *
 * Parameters must match Python implementation exactly:
 *   time=3, memory=65536 (64 MiB), parallelism=4, hashLen=32, saltLen=16
 */

import { argon2id } from "hash-wasm";
import { randomBytes } from "./utils.js";

const ARGON2_TIME = 3;
const ARGON2_MEMORY = 65536; // 64 MiB
const ARGON2_PARALLELISM = 4;
const KEY_LENGTH = 32;
export const SALT_LENGTH = 16;

export interface DerivedKey {
  key: Uint8Array; // 32 bytes
  salt: Uint8Array; // 16 bytes
}

/**
 * Derive a 256-bit key from passphrase using Argon2id.
 * If salt is not provided, generates a random 16-byte salt.
 */
export async function deriveKey(
  passphrase: string,
  salt?: Uint8Array
): Promise<DerivedKey> {
  const actualSalt = salt ?? randomBytes(SALT_LENGTH);

  const hashHex = await argon2id({
    password: passphrase,
    salt: actualSalt,
    iterations: ARGON2_TIME,
    memorySize: ARGON2_MEMORY,
    hashLength: KEY_LENGTH,
    parallelism: ARGON2_PARALLELISM,
    outputType: "hex",
  });

  // Convert hex string to Uint8Array
  const key = new Uint8Array(KEY_LENGTH);
  for (let i = 0; i < KEY_LENGTH; i++) {
    key[i] = parseInt(hashHex.substring(i * 2, i * 2 + 2), 16);
  }

  return { key, salt: actualSalt };
}
